import { Button } from "@/components/ui/button";
import { MapPin, Compass } from "lucide-react";
import type { RadiusKm } from "@/lib/map/config";

interface EmptyStateProps {
  onClearFilters: () => void;
  hasRegionOnly?: boolean;
  // Near me specific props
  nearMeActive?: boolean;
  currentRadius?: RadiusKm;
  onExpandRadius?: () => void;
  onDisableNearMe?: () => void;
}

const EmptyState = ({
  onClearFilters,
  hasRegionOnly,
  nearMeActive,
  currentRadius,
  onExpandRadius,
  onDisableNearMe,
}: EmptyStateProps) => {
  // Special empty state for "Nära mig" mode
  if (nearMeActive && currentRadius) {
    return (
      <div className="text-center py-16 px-4">
        <div className="max-w-md mx-auto space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-muted/40">
            <MapPin className="h-7 w-7 text-muted-foreground/70" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-display font-medium text-foreground">
              Inga platser inom {currentRadius} km
            </h3>
            <p className="text-muted-foreground">
              Prova en större radie eller utforska hela kartan.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {currentRadius < 25 && onExpandRadius && (
              <Button
                variant="default"
                onClick={onExpandRadius}
                className="min-h-[44px]"
              >
                Visa 25 km
              </Button>
            )}
            {onDisableNearMe && (
              <Button
                variant="outline"
                onClick={onDisableNearMe}
                className="min-h-[44px]"
              >
                Visa alla platser
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default empty state - warmer, more editorial
  return (
    <div className="text-center py-20 px-4">
      <div className="max-w-md mx-auto space-y-5">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream/50">
          <Compass className="h-8 w-8 text-muted-foreground/60" />
        </div>
        
        <div className="space-y-3">
          {hasRegionOnly ? (
            <>
              <h3 className="text-lg font-display font-medium text-foreground">
                Inga platser här ännu
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Vi bygger långsamt och lokalt. Fler platser är på väg.
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-display font-medium text-foreground">
                Inga träffar just nu
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Prova att zooma ut kartan eller testa en annan kategori.
              </p>
            </>
          )}
        </div>

        <Button
          variant="outline"
          onClick={onClearFilters}
          className="min-h-[44px] border-border/50"
        >
          Rensa filter
        </Button>
      </div>
    </div>
  );
};

export default EmptyState;
